<!-- 
  @component
  ### Drawer, side panel for complex dialogs, also sheet on mobile
  eg: https://www.figma.com/design/n0GA6zpBrMsZTHXS66c9rG/DS02.-Components?node-id=1088-28312&t=eUJ38OtuNIA2QZiC-0
-->
<script lang="ts">
  import { BREAKPOINTS } from '$lib/constants';
  import { browser } from '$app/environment';
  import { createEventDispatcher, getContext } from 'svelte';
  import { uuidv4 } from '$lib/utils';
  import type { App } from '$lib/types';

  import Skeleton from '$components/mds/Dialogs/_Skeleton.svelte';
  import Grabber from './components/Grabber.svelte';
  import { DialogStatus } from '$components/mds/Dialogs/types';
  import { drawers } from '$components/mds/Dialogs/stores';
  import { closeSheetPanel, scrollerSheetPanel } from '$lib/directives/sheetPanel';

  import IconButton from '$mds/Buttons/IconButton';
  import { faXmark, faArrowLeft } from '@fortawesome/pro-solid-svg-icons';

  export let drawer: Skeleton | undefined = undefined;
  export let title: string = '';
  export let isOpen = false;
  export let backIcon = false;
  export let allowClickOutside: undefined | boolean = true;
  export let scrollerEl: HTMLElement | undefined = undefined;
  export let scrollerClass = 'px-5 sm:px-6 pb-5 sm:pb-0';
  export let scrollerMinHeight = 'min-h-[30vh]';
  export let closeButton = true;
  export let closeButtonVariant: 'ghost' | 'secondary' = 'ghost';
  export let mobileCloseButton = false;
  export let focusOnOpen = false;
  export let fullHeightSheet = false;

  export let alignment: 'left' | 'right' = 'right';

  let className = '';
  export { className as class };
  export let contentClass = 'p-1';
  export let outerClass = '';

  const dispatch = createEventDispatcher();
  const uuid = uuidv4();
  const { innerWidth } = getContext<App>('app');

  const onOpenDelay = 500;
  const onCloseDelay = 500;

  let dialogEl: HTMLDialogElement;
  let status: DialogStatus = DialogStatus.Closed;
  let focusReady = focusOnOpen;

  $: isMobile = $innerWidth < BREAKPOINTS.sm;
  $: isSheetLayout = isMobile;
  $: stackDepth = Math.max($drawers.length - 1 - $drawers.indexOf(uuid), 0);
  $: isTopOfStack = $drawers[$drawers.length - 1] === uuid;

  const drawerClasses = {
    right: 'sm:ml-auto sm:origin-left',
    left: 'sm:mr-auto sm:origin-right'
  };
  const drawerClosedClasses = {
    right: 'sm:translate-x-full sm:translate-y-0 !opacity-0',
    left: 'sm:-translate-x-full sm:translate-y-0 !opacity-0'
  };
  const drawerOpenClasses = {
    right: 'sm:translate-x-[calc(-5%*var(--stack-depth,0))] sm:translate-y-0',
    left: 'sm:-translate-x-[calc(-10%*var(--stack-depth,0))] sm:translate-y-0'
  };

  function addToStack() {
    if (drawer) {
      if ($drawers.indexOf(uuid) < 0) {
        drawers.update(drawers => [...drawers, uuid]);
      }
    }
  }

  function removeFromStack() {
    if (drawer) {
      drawers.set([...$drawers.filter(drawer => drawer !== uuid)]);
    }
  }

  $: {
    if (browser && allowClickOutside && isSheetLayout && dialogEl && drawer && isOpen) {
      closeSheetPanel(dialogEl, { onClose: drawer.closeModal });
    }
  }
</script>

<Skeleton
  bind:dialogEl
  bind:this={drawer}
  bind:isOpen
  bind:status
  id={uuid}
  {onOpenDelay}
  {onCloseDelay}
  {allowClickOutside}
  class="sheet {outerClass} sm:rounded-sheet origin-top
    opacity-[var(--sheet-opacity)] duration-500 sm:origin-left
    {status === 'open' ? 'ease-smooth-out' : 'ease-expo-in'}
    {status === 'open'
    ? isTopOfStack
      ? `translate-y-[var(--sheet-y)] scale-100 sm:translate-x-0 ${drawerOpenClasses[alignment]}`
      : `translate-y-[calc(-3%*var(--stack-depth)+var(--sheet-y,0px))] scale-[calc(100%-5%*var(--stack-depth,0))] ${drawerOpenClasses[alignment]}`
    : `translate-y-full ${drawerClosedClasses[alignment]}`}
    h-full max-h-full w-[552px] max-w-full pt-12 transition-all will-change-[transform,opacity] sm:p-4 {drawerClasses[
    alignment
  ]}"
  style="--stack-depth: {stackDepth};"
  on:open={() => {
    addToStack();
    dispatch('open');
    setTimeout(() => {
      scrollerEl?.scrollTo(0, 0);
    });
  }}
  on:opened={() => {
    focusReady = true;
    dispatch('opened');
  }}
  on:closing={() => {
    setTimeout(() => {
      removeFromStack();
    }, onCloseDelay / 2);
    dispatch('closing');
  }}
  on:closed={() => {
    focusReady = focusOnOpen;
    dispatch('closed');
  }}
  on:clickOutside
>
  <section
    inert={!focusReady && isSheetLayout}
    class="{isSheetLayout ? 'sheet shadow-sheet' : 'shadow-modal h-full'} {isSheetLayout && fullHeightSheet
      ? 'h-full'
      : ''} bg-ondark-base border-ondark-subtle sm:shadow-modal flex flex-col border border-b-0 sm:rounded-2xl sm:border-b {contentClass} {className} {contentClass.includes(
      'bg-'
    )
      ? ''
      : 'bg-ondark-base'}"
  >
    <!-- Header -->
    <header class="z-1 relative px-5 py-4 sm:px-6">
      {#if allowClickOutside}
        <Grabber />
      {/if}

      <div class="text-ondark-primary flex items-center justify-between gap-1">
        {#if backIcon}
          <IconButton
            variant="ghost"
            class="hidden hover:bg-transparent sm:block"
            icon={faArrowLeft}
            size="sm"
            on:click={() => {
              drawer?.closeModal();
            }}
          />
        {/if}

        {#if $$slots.title}
          <slot name="title" />
        {:else if title}
          <h3 class="text-body-md sm:text-display-xs mr-auto">
            {title}
          </h3>
        {/if}

        {#if $$slots.icons}
          <slot name="icons" />
        {/if}

        <IconButton
          variant={closeButtonVariant}
          class="{$$slots.icons ? '' : 'ml-auto'} {closeButtonVariant === 'ghost'
            ? 'hover:bg-transparent'
            : ''} {closeButton ? 'sm:flex' : 'sm:hidden'} {!mobileCloseButton ? 'hidden' : ''}"
          icon={faXmark}
          size="sm"
          on:click={() => {
            drawer?.closeModal();
          }}
        />
      </div>
    </header>

    <!-- Main -->
    <div
      bind:this={scrollerEl}
      class="scrollbar-hide sm:scrollbar-default flex-1 overflow-y-auto rounded-[inherit] {scrollerMinHeight} {scrollerClass}"
      use:scrollerSheetPanel
      on:scroll
    >
      <slot />
    </div>

    <!-- Footer -->
    {#if $$slots.actions}
      <footer
        class="bg-ondark-low shadow-negative z-1 grid grid-cols-2 items-center gap-4 p-3 sm:rounded-b-2xl sm:p-5 sm:pb-3"
      >
        <slot name="actions" />
      </footer>
    {/if}
  </section>
</Skeleton>
